import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { DocumentBlocker } from '@rossum/api-client/queues';
import {
  Box,
  Divider,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { GridColDef } from '@rossum/ui/x-data-grid-pro';
import { generateDocumentListUrl } from '../../../../../document-list-base/helpers/generateDocumentListUrl';
import { AutomationInsightssRowModel } from '../../../insights/types';
import { getBlockerTranslation, getLimitedAnnotationIds } from '../../utils';

type RenderDetailPanelProps = {
  blockers: DocumentBlocker[];
  columns: GridColDef<AutomationInsightssRowModel>[];
  queueId: number;
};
const hasComputedWidth = (
  column: GridColDef<AutomationInsightssRowModel>
): column is GridColDef<AutomationInsightssRowModel> & {
  computedWidth: number;
} => 'computedWidth' in column;

const getAvailableWidth = (column: GridColDef<AutomationInsightssRowModel>) =>
  hasComputedWidth(column) ? column.computedWidth : (column.width ?? 0);

const sizeMapper = {
  type: c => ({
    typeWidth: getAvailableWidth(c),
  }),
  label: c => ({
    labelWidth: getAvailableWidth(c),
  }),
  blockers: c => ({
    blockersWidth: getAvailableWidth(c),
  }),
  precision: c => ({
    precisionWidth: getAvailableWidth(c),
  }),
} as const satisfies Partial<
  Record<
    keyof AutomationInsightssRowModel,
    (column: GridColDef<AutomationInsightssRowModel>) => Record<string, number>
  >
>;

const hasSizeMapper = (key: string): key is keyof typeof sizeMapper =>
  key in sizeMapper;

export const RenderDetailPanel = ({
  blockers,
  columns,
  queueId,
}: RenderDetailPanelProps) => {
  const intl = useIntl();

  if (blockers.length === 0)
    return (
      <Stack
        data-cy="empty-detail-panel"
        sx={{ p: 2, justifyContent: 'center', alignItems: 'center' }}
      >
        <Typography variant="body2">
          {intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.blockedDocumentCounts.emptyState',
          })}
        </Typography>
      </Stack>
    );

  const {
    typeWidth,
    labelWidth,
    importanceWidth,
    blockersWidth,
    errorRateWidth,
    actionWidth,
  } = columns.reduce(
    (acc, curr) => {
      // we replicate the exact same column widths of DataGrid within this custom component
      const size = hasSizeMapper(curr.field)
        ? sizeMapper[curr.field]?.(curr)
        : null;

      if (size) return { ...acc, ...size };

      return acc;
    },
    {
      typeWidth: 0,
      labelWidth: 0,
      importanceWidth: 0,
      blockersWidth: 0,
      errorRateWidth: 0,
      actionWidth: 0,
    }
  );

  return (
    <Stack
      divider={<Divider />}
      component={Paper}
      elevation={2}
      sx={{ borderRadius: 0, border: 'none', boxShadow: 'none' }}
    >
      {blockers.map(({ blocker, documentCount, exampleAnnotationIds }) => {
        return (
          <Stack key={blocker} direction="row" sx={{ py: 1 }}>
            {typeWidth ? <Box sx={{ width: typeWidth }} /> : null}
            <Typography
              variant="h6"
              sx={{ width: labelWidth, px: 1.25 }}
              data-cy={`${blocker}-blocked-label`}
            >
              {getBlockerTranslation({ intl, key: blocker })}
            </Typography>
            <Box sx={{ width: importanceWidth }} />
            <Stack
              direction="row"
              sx={{
                width: blockersWidth,
                justifyContent: 'flex-end',
                px: 1.25,
                gap: 1,
              }}
            >
              <MuiLink
                component={RouterLink}
                color="text.primary"
                sx={{ flex: 0.2, textAlign: 'right' }}
                to={generateDocumentListUrl({
                  filtering: [
                    {
                      field: 'queue',
                      operator: 'isAnyOf',
                      value: [queueId],
                    },
                    {
                      field: 'annotation',
                      operator: 'isAnyOf',
                      value: getLimitedAnnotationIds(exampleAnnotationIds),
                    },
                  ],
                  level: 'queue',
                  view: 'documents',
                })}
                data-cy={`${blocker}-blocked-count`}
              >
                {documentCount}
              </MuiLink>
            </Stack>
            {errorRateWidth ? <Box sx={{ width: errorRateWidth }} /> : null}
            {actionWidth ? <Box sx={{ width: actionWidth }} /> : null}
          </Stack>
        );
      })}
    </Stack>
  );
};
